import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, qs } from '../lib/api';
import type { Page } from '../lib/api';
import { date, dateTime, humanise, mobile as fmtMobile } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Badge, Button, Chips, Notice, SearchInput, Select } from '../components/ui';

interface CrmRow {
  id: string;
  name: string;
  mobile: string;
  stage: string;
  interest: 'UNKNOWN' | 'INTERESTED' | 'NOT_INTERESTED';
  interestSetAt: string | null;
  followUpAt: string | null;
  interestedSite: string | null;
  siteVisitAt: string | null;
  siteVisitConfirmedAt: string | null;
  handedOverToAgent: boolean;
  createdAt: string;
  agent: { id: string; referralCode: string; user: { displayName: string | null; mobile: string } };
}

interface AgentRow {
  id: string;
  referralCode: string;
  user: { displayName: string | null; mobile: string };
}

type View = '' | 'today' | 'overdue';

/**
 * Where a visit stands, derived rather than stored.
 *
 * The app derives the same thing the same way, from the date and the
 * confirmation, so a status can never contradict the date printed beside it.
 * A stored status can, and eventually does.
 */
function visitState(r: CrmRow): { label: string; tone: 'green' | 'amber' | 'red' | 'neutral' } {
  if (r.siteVisitConfirmedAt) return { label: `Confirmed ${date(r.siteVisitConfirmedAt)}`, tone: 'green' };
  if (!r.siteVisitAt) return { label: 'Not scheduled', tone: 'neutral' };

  const when = new Date(r.siteVisitAt);
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfTomorrow = new Date(startOfToday);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);

  if (when < startOfToday) return { label: `Overdue · ${date(when)}`, tone: 'red' };
  if (when < startOfTomorrow) return { label: `Today · ${dateTime(when)}`, tone: 'amber' };
  return { label: dateTime(when), tone: 'neutral' };
}

/** FR-CRM-01…06 — the leads partners are working, and where each one is stuck. */
export function CrmPage() {
  const { get, set } = useListParams();
  const search = get('search');
  const stage = get('stage');
  const interest = get('interest');
  const agentId = get('agent');
  const view = (get('view') || '') as View;

  // Named so the header can say whose leads these are; a referral code alone
  // means nothing to whoever is reading the screen.
  const agent = useQuery({
    queryKey: ['crm-agent', agentId],
    queryFn: () => api.get<Page<AgentRow>>(`/admin/agents${qs({ limit: 200 })}`),
    enabled: Boolean(agentId),
    select: (page) => page.items.find((a) => a.id === agentId),
  });

  const columns: Array<Column<CrmRow>> = [
    {
      key: 'customer',
      header: 'Lead',
      render: (r) => (
        <div>
          <div className="cell-strong">{r.name}</div>
          <div className="cell-muted">{fmtMobile(r.mobile)}</div>
        </div>
      ),
    },
    {
      key: 'agent',
      header: 'Partner',
      render: (r) => (
        <div>
          <button
            className="linklike"
            onClick={(e) => {
              e.stopPropagation();
              set('agent', r.agent.id === agentId ? '' : r.agent.id);
            }}
          >
            {r.agent.user.displayName ?? fmtMobile(r.agent.user.mobile)}
          </button>
          <div className="cell-mono">{r.agent.referralCode}</div>
        </div>
      ),
    },
    { key: 'stage', header: 'Stage', render: (r) => <Badge tone="blue">{humanise(r.stage)}</Badge> },
    {
      key: 'interest',
      header: 'Interested',
      render: (r) =>
        r.interest === 'INTERESTED' ? (
          <Badge tone="green">Yes</Badge>
        ) : r.interest === 'NOT_INTERESTED' ? (
          <Badge tone="red">No</Badge>
        ) : (
          <span className="cell-muted">Not asked</span>
        ),
    },
    {
      key: 'followUp',
      header: 'Follow up',
      render: (r) =>
        r.followUpAt ? date(r.followUpAt) : <span className="cell-muted">—</span>,
    },
    { key: 'site', header: 'Interested in', render: (r) => r.interestedSite ?? <span className="cell-muted">—</span> },
    {
      key: 'visit',
      header: 'Site visit',
      render: (r) => {
        const s = visitState(r);
        return s.tone === 'neutral' ? <span className="cell-muted">{s.label}</span> : <Badge tone={s.tone}>{s.label}</Badge>;
      },
    },
    {
      key: 'handover',
      header: 'Handled by',
      render: (r) =>
        r.handedOverToAgent ? <Badge tone="amber">Partner</Badge> : <Badge tone="purple">Assistant</Badge>,
    },
    { key: 'created', header: 'Added', render: (r) => <span className="cell-muted">{date(r.createdAt)}</span> },
  ];

  return (
    <div className="stack">
      <Chips<View>
        value={view}
        onChange={(v) => set('view', v)}
        options={[
          { value: '', label: 'All leads' },
          { value: 'today', label: "Today's visits" },
          { value: 'overdue', label: 'Overdue' },
        ]}
      />

      {view === 'overdue' && (
        <Notice tone="warn" title="A visit date that has passed without a confirmation">
          Either it happened and nobody marked it, or it did not happen at all. Both are worth a call to the partner —
          the lead is sitting in neither state.
        </Notice>
      )}

      {agentId && (
        <Notice
          tone="info"
          title={
            agent.data
              ? `Only ${agent.data.user.displayName ?? fmtMobile(agent.data.user.mobile)}'s leads`
              : 'Filtered to one partner'
          }
        >
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <span>Everyone else's leads are hidden.</span>
            <Button size="sm" variant="ghost" onClick={() => set('agent', '')}>
              Show all partners
            </Button>
            {agent.data && (
              <Link to={`/team?partner=${agentId}`}>
                <Button size="sm" variant="ghost">
                  Their team
                </Button>
              </Link>
            )}
          </div>
        </Notice>
      )}

      <DataTable<CrmRow>
        queryKey={['crm', search, stage, interest, agentId, view]}
        path="/admin/crm-customers"
        params={{ search, stage, interest, agentId, visits: view }}
        columns={columns}
        emptyIcon="☎"
        emptyTitle={
          view === 'today'
            ? 'No site visits scheduled for today'
            : view === 'overdue'
              ? 'Nothing overdue'
              : 'No CRM leads yet'
        }
        emptyBody="Partners add leads in the app; the assistant works them up to a confirmed site visit (FR-CRM-01/02)."
        toolbar={
          <>
            <SearchInput value={search} onChange={(v) => set('search', v)} placeholder="Name or mobile…" />
            <Select
              value={stage}
              placeholder="Any stage"
              onChange={(e) => set('stage', e.target.value)}
              options={['NEW', 'QUALIFYING', 'NEGOTIATING', 'VISIT_CONFIRMED', 'CLOSED'].map((v) => ({
                value: v,
                label: humanise(v),
              }))}
            />
            <Select
              value={interest}
              placeholder="Any answer"
              onChange={(e) => set('interest', e.target.value)}
              options={[
                { value: 'INTERESTED', label: 'Interested' },
                { value: 'NOT_INTERESTED', label: 'Not interested' },
                { value: 'UNKNOWN', label: 'Not asked' },
              ]}
            />
          </>
        }
      />
    </div>
  );
}
